import { useCallback, useMemo, useState } from 'react';
import t from 'react-translate';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';

export type RoleplayUsageValue = {
  enabled: boolean,
  approvedTypes: RolePlayInteractionMode[],
};

export const defaultRoleplayUsageValue: RoleplayUsageValue = {
  enabled: false,
  approvedTypes: [],
};

export const isRoleplayUsageValid = (value: RoleplayUsageValue): boolean => (
  !value.enabled || value.approvedTypes.length > 0
);

// Local state + "≥1 type when enabled" validation. Save-agnostic: consumers call validate() before saving.
const useRoleplayUsageForm = (initialValue: RoleplayUsageValue = defaultRoleplayUsageValue) => {
  const [value, setValue] = useState<RoleplayUsageValue>(initialValue);
  const [showError, setShowError] = useState(false);

  const isValid = isRoleplayUsageValid(value);

  const onChange = useCallback((newValue: RoleplayUsageValue) => {
    setValue(newValue);
    if (isRoleplayUsageValid(newValue)) {
      setShowError(false);
    }
  }, []);

  const validate = useCallback(() => {
    const valid = isRoleplayUsageValid(value);
    setShowError(!valid);
    return valid;
  }, [value]);

  const error = useMemo(() => (
    showError && !isValid ? t.AI_ROLEPLAY.COURSE_LEVEL_MANAGEMENT.ERROR.SELECT_AT_LEAST_ONE_TYPE() : null
  ), [showError, isValid]);

  return {
    value,
    setValue,
    onChange,
    isValid,
    validate,
    error,
  };
};

export default useRoleplayUsageForm;
